import { Component, input, model, signal, inject, effect } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, finalize, map, of } from 'rxjs';
import _ from 'lodash';
import { ItemMaster, ItemMasterFilter } from '../../../models/item-master';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ItemDetailsFormGroup } from '../../../models/engineering/item-consumable-details';
import { ScalingMethod } from '../../../models/engineering/enum/ScalingMethod.enum';
import { IngredientUnit } from '../../../models/engineering/enum/IngredientUnit.enum';
import { LocaleHelperService } from '../../../services/localehelperservice.service';

@Component({
  selector: 'app-item-details-viewer',
  templateUrl: './item-details-viewer.component.html',
  styleUrl: './item-details-viewer.component.scss',
})
export class ItemDetailsViewerComponent {
  private readonly itemProxyService = inject(ItemMasterProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly localeHelper = inject(LocaleHelperService);

  inventoryTypeReference = input<string | undefined>();
  canEdit = input<boolean>(true);
  itemDetailsFormGroups = model<FormArray<FormGroup<ItemDetailsFormGroup>>>(new FormArray<FormGroup<ItemDetailsFormGroup>>([]));

  isLoading = signal(0);

  scalingMethodOptions = signal<{ label: string; value: ScalingMethod }[]>([
    { label: 'By Weight', value: ScalingMethod.ByWeight },
    { label: 'By Volume', value: ScalingMethod.ByVolume },
    { label: 'By Liquor Volume', value: ScalingMethod.ByLiquorVolume },
    { label: 'Fixed', value: ScalingMethod.Fixed },
    { label: 'By Liquor Ratio', value: ScalingMethod.ByLiquorRation },
  ]);

  ingredientUnitOptions = signal<{ label: string; value: IngredientUnit }[]>([
    { label: 'Percent', value: IngredientUnit.Percent },
    { label: 'G/L', value: IngredientUnit.GramsPerLiter },
  ]);

  constructor() {
    effect(() => {
      const formGroups = this.itemDetailsFormGroups();
      if (formGroups && formGroups.length > 0) {
        this.processExistingItems();
      }
    });

    this.initializeEnumOptions();

    this.translateService.onLangChange.subscribe(() => {
      this.initializeEnumOptions();
    });
  }

  private processExistingItems(): void {
    const formGroups = this.itemDetailsFormGroups();
    formGroups.controls.forEach((formGroup) => {
      const itemControl = formGroup.get('item');
      if (itemControl && itemControl.value) {
        const currentItem = itemControl.value as ItemMaster;

        if (!('itemDisplayNameLocalized' in currentItem)) {
          const itemWithLocalized: ItemMaster = {
            ...currentItem,
          };
          itemControl.setValue(itemWithLocalized, { emitEvent: false });
        }
      }
    });
  }

  private initializeEnumOptions(): void {
    const currentLang = this.translateService.currentLang || this.translateService.getDefaultLang();

    if (!currentLang) {
      return;
    }

    this.scalingMethodOptions.set([
      {
        label: this.translateService.instant('ScalingMethod_ByWeight') || 'By Weight',
        value: ScalingMethod.ByWeight,
      },
      {
        label: this.translateService.instant('ScalingMethod_ByVolume') || 'By Volume',
        value: ScalingMethod.ByVolume,
      },
      {
        label: this.translateService.instant('ScalingMethod_ByLiquorVolume') || 'By Liquor Volume',
        value: ScalingMethod.ByLiquorVolume,
      },
      {
        label: this.translateService.instant('ScalingMethod_Fixed') || 'Fixed',
        value: ScalingMethod.Fixed,
      },
      {
        label: this.translateService.instant('ScalingMethod_ByLiquorRation') || 'By Liquor Ratio',
        value: ScalingMethod.ByLiquorRation,
      },
    ]);

    this.ingredientUnitOptions.set([
      {
        label: this.translateService.instant('IngredientUnit_Percent') || 'Percent',
        value: IngredientUnit.Percent,
      },
      {
        label: this.translateService.instant('IngredientUnit_GramsPerLiter') || 'G/L',
        value: IngredientUnit.GramsPerLiter,
      },
    ]);
  }

  private updateLoadingState(isLoading: boolean): void {
    this.isLoading.update((value) => (isLoading ? value + 1 : value - 1));
  }

  public fetchItemMasters = (query: string): Observable<ItemMaster[]> => {
    // The template used to guard this call with canEdit(); the guard lives here now.
    if (!this.canEdit()) return of([]);

    const searchCommand: ItemMasterFilter = {
      inventoryTypeReference: this.inventoryTypeReference(),
      name: query,
    };

    this.updateLoadingState(true);

    return this.itemProxyService.postItemSearch(searchCommand).pipe(
      finalize(() => this.updateLoadingState(false)),
      map((response) =>
        (response.data?.items || []).map((item) => ({
          ...item,
          itemDisplayNameLocalized: this.localeHelper.getLocalizedValue(item.itemDisplayName),
        })),
      ),
    );
  };

  addElement(): void {
    const formGroup = new FormGroup<ItemDetailsFormGroup>({
      item: new FormControl<ItemMaster | null>(null, [Validators.required]),
      isPreferred: new FormControl<boolean>(false, [Validators.required]),
      ingredientUnit: new FormControl<IngredientUnit | null>(null),
      scalingMethod: new FormControl<ScalingMethod | null>(null),
      quantity: new FormControl<number | null>(null, [Validators.required]),
    });

    if (!this.canEdit()) {
      formGroup.disable();
    }

    this.itemDetailsFormGroups().push(formGroup);
  }

  removeElement(index: number): void {
    const formGroups = this.itemDetailsFormGroups();
    if (index >= 0 && index < formGroups.length) {
      formGroups.removeAt(index);
    }
  }

  getScalingMethodLabel(value: ScalingMethod | null): string {
    if (_.isNil(value)) return '';
    const option = this.scalingMethodOptions().find((opt) => opt.value === value);
    return option?.label || '';
  }

  getIngredientUnitLabel(value: IngredientUnit | null): string {
    if (_.isNil(value)) return '';
    const option = this.ingredientUnitOptions().find((opt) => opt.value === value);
    return option?.label || '';
  }

  getItemDisplayName(item: ItemMaster | null): string {
    if (!item) return '';
    return this.localeHelper.getLocalizedValue(item.itemDisplayName);
  }
}
